import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { executePerps } from "../../functions/Contract";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import UpdateActionsModal from "../Modals/UpdateActionsModal";
import TradeDetailModal from "../Modals/TradeDetailModal";
import { useWalletConnection } from "../../../hooks/useWalletConnection";
import { closeTradeMarketEvm } from "../../../evm/services/tradingEvmService";
import { getMarketName } from "../../../utils/marketUtils";
import { getMarketByMarketIndex } from "../../../config/markets";
import { useMarketPrice } from "../../../hooks/useMarketPrices";
import { getNibiruSDK, getContractAddresses } from "../../../config/network";
import { formatPriceWithSignificantDecimals, formatWithSubscript } from "../../../Components/utils/formatter";
import { WhiteButton } from "../../../Components/Buttons/WhiteButton";
import { RedButton } from "../../../Components/Buttons/RedButton";
import { TRADES_GRID_COLUMNS, TRADES_MIN_WIDTH, TRADES_ROW_PADDING_X } from "./tableLayout";

const nibiru = getNibiruSDK();
const formatDate = (timestamp) => {
  try {
    console.log("Original timestamp:", timestamp, "Type:", typeof timestamp);
    
    if (!timestamp) {
      return { date: "Recent", time: "" };
    }

    let date;
    
    // Handle different timestamp formats
    if (typeof timestamp === 'string') {
      // Remove any extra characters and parse
      const cleanTimestamp = timestamp.trim();
      
      if (cleanTimestamp.includes('T') || cleanTimestamp.includes('-')) {
        // ISO string format
        date = new Date(cleanTimestamp);
      } else {
        // Numeric string - try parsing as number
        const numTimestamp = parseInt(cleanTimestamp);
        if (!isNaN(numTimestamp)) {
          // Handle nanosecond timestamps (19 digits) - convert to milliseconds
          if (numTimestamp.toString().length >= 19) {
            // Nanoseconds - divide by 1,000,000 to get milliseconds
            date = new Date(Math.floor(numTimestamp / 1000000));
          } else if (numTimestamp > 1e12) {
            // Already in milliseconds
            date = new Date(numTimestamp);
          } else {
            // Seconds - multiply by 1000 to get milliseconds
            date = new Date(numTimestamp * 1000);
          }
        }
      }
    } else if (typeof timestamp === 'number') {
      // Handle nanosecond timestamps (19 digits) - convert to milliseconds
      if (timestamp.toString().length >= 19) {
        // Nanoseconds - divide by 1,000,000 to get milliseconds
        date = new Date(Math.floor(timestamp / 1000000));
      } else if (timestamp > 1e12) {
        // Already in milliseconds
        date = new Date(timestamp);
      } else {
        // Seconds - multiply by 1000 to get milliseconds
        date = new Date(timestamp * 1000);
      }
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      // Try to convert to string first
      date = new Date(String(timestamp));
    }

    console.log("Parsed date:", date);

    // Validate the date
    if (!date || isNaN(date.getTime())) {
      console.error("Invalid date created from timestamp:", timestamp);
      return { date: "Recent", time: "" };
    }

    // Format the date and time
    const dateStr = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    console.log("Formatted result:", { date: dateStr, time: timeStr });
    return { date: dateStr, time: timeStr };

  } catch (error) {
    console.error("Error formatting date:", error, "Original timestamp:", timestamp);
    return { date: "Recent", time: "" };
  }
};

const TradesRow = ({
  trade,
  onTradeClick,
  showPnlInfo = true,
  autoOpenDetail = false,
}) => {
  const currentPrice = useSelector((state) => state.trade.currentPrice);
  const { account, walletType, isEvm } = useWalletConnection();
  const [modalShow, setModalShow] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const dispatch = useDispatch();

  // Auto-open detail modal when navigated from AllTradesPage
  useEffect(() => {
    if (autoOpenDetail) {
      setDetailModalOpen(true);
    }
  }, [autoOpenDetail]);

  // Use the reusable hook to get market-specific price
  // Only fetch if trade is open and has a market_index
  const { price: marketPrice } = useMarketPrice(
    trade?.is_open && trade?.market_index ? trade.market_index : null,
    30000 // Update every 30 seconds (not continuously)
  );

  const getTradeIndexNumber = (userTradeIndex) => {
    if (!userTradeIndex) return null;

    const match = userTradeIndex.toString().match(/\((\d+)\)/);
    if (match && match[1]) {
      const tradeId = parseInt(match[1], 10);
      return isNaN(tradeId) ? null : tradeId;
    }

    const directNumber = parseInt(userTradeIndex, 10);
    return isNaN(directNumber) ? null : directNumber;
  };

  const lowPriceTokenIds = new Set(["pump", "pengu", "bonk", "shib", "nibi"]);
  const [fallbackLiquidationPrice, setFallbackLiquidationPrice] = useState(null);
  const [isLoadingTpSl, setIsLoadingTpSl] = useState(false);

  // Formatter for entry prices, with special handling for very low-value tokens
  // to match the CoinGecko-style 0.0₅7751 display.
  const formatEntryPrice = (value, isLowPriceToken) => {
    if (value === null || value === undefined || value === "") return "-";

    const num = parseFloat(value);
    if (isNaN(num)) return "-";

    const abs = Math.abs(num);

    if (isLowPriceToken && abs > 0 && abs < 1) {
      // Convert to a fixed decimal string with plenty of precision
      const raw = abs.toFixed(20);
      const decimalPart = raw.split(".")[1] || "";

      // Count leading zeros after "0."
      let zeroCount = 0;
      for (let i = 0; i < decimalPart.length; i++) {
        if (decimalPart[i] === "0") {
          zeroCount += 1;
        } else {
          break;
        }
      }

      // Remaining significant digits after the zeros (limit to 3 digits)
      const remaining = decimalPart.slice(zeroCount).slice(0, 3);

      if (!remaining) {
        // Fallback: if no remaining digits, show a high-precision small decimal
        return formatPriceWithSignificantDecimals(num, 8, true);
      }

      return (
        <>
          $0.0
          <sub>{zeroCount}</sub>
          {remaining}
        </>
      );
    }

    // Default formatting for non-low-price or >= 1 tokens
    return formatPriceWithSignificantDecimals(num, 8, true);
  };

  const formatCollateralAmount = (amount, denom = "USDC") => {
    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return { amount: "0", currency: denom };
      
      const formattedAmount = (numAmount / 1000000).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return { amount: `$${formattedAmount}`, currency: denom };
    } catch (error) {
      console.error("Error formatting collateral amount:", error);
      return { amount: "0", currency: denom };
    }
  };

  const formatAmount = (amount, leverage, denom = "USDC") => {
    try {
      const numAmount = parseFloat(amount);
      const numLeverage = parseFloat(leverage);
      
      if (isNaN(numAmount) || isNaN(numLeverage)) return { amount: "0", currency: denom };
      
      const totalSize = (numAmount * numLeverage) / 1000000;
      const formattedSize = totalSize.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return { amount: `$${formattedSize}`, currency: denom };
    } catch (error) {
      console.error("Error formatting amount:", error);
      return { amount: "0", currency: denom };
    }
  };

  // (formatAmountWithCurrency removed – no longer used)

  const formatPnL = (collateralAmount, isOpen, openPrice, isLong, leverage, hasMoved, onchainPnl) => {
    // Only show PnL for open trades with a reliable market-specific price.
    // If we don't have a marketPrice yet, we treat PnL as "not ready" so the UI
    // can show a skeleton loader instead of a misleading interim value.
    if (!isOpen) return { pnl: "$0.00", percentage: "0.00", isProfit: false, isReady: false };

    // Prefer the contract's own authoritative PnL (bulk-fetched for the whole
    // table from sai-keeper — see AllTradesTable) over the client-side
    // price-tick estimate below. The on-chain figure includes fees and is
    // the same number TradeDetailModal shows; the estimate below is fee-blind
    // and can show sub-cent price-feed noise as if it were meaningful PnL.
    // Falls through to the estimate only if the bulk fetch didn't cover this
    // trade (e.g. still loading, or the request failed).
    if (onchainPnl) {
      const { pnlCollateral } = onchainPnl;
      const absPnl = Math.abs(pnlCollateral);
      const pnlRoundsToZero = absPnl > 0 && Number(absPnl.toFixed(2)) === 0;
      // Percentage is net-PnL over margin, matching how TradeDetailModal
      // derives its headline percentage — NOT the indexer's raw `pnlPct`,
      // which is price-movement-only and disagrees with the after-fees figure.
      const marginUsd = parseFloat(collateralAmount) / (10 ** process.env.REACT_APP_USDC_DECIMALS);
      const pct = marginUsd > 0 ? (pnlCollateral / marginUsd) * 100 : 0;
      return {
        pnl: pnlRoundsToZero
          ? formatWithSubscript(absPnl, { sigDigits: 3 })
          : absPnl.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        percentage: Math.abs(pct).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        isProfit: pnlCollateral >= 0,
        isReady: true,
      };
    }

    const openPriceNum = parseFloat(openPrice);
    const currentPriceNum =
      marketPrice !== null && marketPrice !== undefined ? marketPrice : null;
    const marginNum = parseFloat(collateralAmount) / (10 ** process.env.REACT_APP_USDC_DECIMALS);
    const leverageNum = parseFloat(leverage);

    // If we don't yet have a market-specific price (or other required data),
    // mark PnL as not ready – we'll render a skeleton in the UI.
    if (!openPriceNum || !currentPriceNum || !marginNum || !leverageNum) {
      return { pnl: "$0.00", percentage: "0.00", isProfit: false, isReady: false };
    }

    // Position Size = Margin × Leverage
    const positionSize = marginNum * leverageNum;
    
    // Asset Quantity = Position Size ÷ Entry Price
    const assetQuantity = positionSize / openPriceNum;
    
    let pnlUsd;
    let isProfit;

    if (isLong) {
      // Long position: PnL = (Current - Entry) × Quantity
      pnlUsd = (currentPriceNum - openPriceNum) * assetQuantity;
      isProfit = currentPriceNum > openPriceNum;
    } else {
      // Short position: PnL = (Entry - Current) × Quantity
      pnlUsd = (openPriceNum - currentPriceNum) * assetQuantity;
      isProfit = currentPriceNum < openPriceNum;
    }

    // Calculate percentage: (PnL / Margin) × 100
    const pnlPercentage = (pnlUsd / marginNum) * 100;

    const absPnlUsd = Math.abs(pnlUsd);

    // Before any price movement, keep 2 decimals (typically shows $0.00 right after opening).
    // After movement, a nonzero PnL that still rounds to $0.00 at 2 decimals gets the
    // CoinGecko-style subscript treatment instead of trailing out to 6 decimal places.
    const pnlRoundsToZero = hasMoved && absPnlUsd > 0 && Number(absPnlUsd.toFixed(2)) === 0;
    const pnl = pnlRoundsToZero
      ? formatWithSubscript(absPnlUsd, { sigDigits: 3 })
      : absPnlUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return {
      pnl,
      percentage: Math.abs(pnlPercentage).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      isProfit: isProfit,
      isReady: true,
    };
  };

  // getMarketName is now imported from marketUtils

  // Trade side (BUY/SELL)
  const getTradeSide = (isLong) => (isLong ? "BUY" : "SELL");

  // Order type (MARKET/LIMIT)
  const getOrderType = (tradeType) =>
    tradeType === "limit" ? "LIMIT" : "MARKET";

  // Format leverage with x
  const formatLeverage = (leverage) => {
    if (leverage === undefined || leverage === null) {
      return "1.00x";
    }
    const leverageNum = parseFloat(leverage);
    return isNaN(leverageNum) ? "1.00x" : `${leverageNum.toFixed(2)}x`;
  };

  // Format date with time - try trade_info first, then fall back to open_timestamp
  console.log("Trade object for date:", trade);
  const timestamp = trade.trade_info?.last_oi_update_ts || trade.open_timestamp;
  // console.log("Selected timestamp for formatting:", timestamp);
  const dateTimeFormatted = timestamp ? formatDate(timestamp) : { date: "Recent", time: "" };

  // Get market pair
  const market = getMarketName(trade.market_index);

  // Format entry price (CoinGecko-style for very low-price tokens)
  const marketForEntry = getMarketByMarketIndex(trade.market_index);
  const isLowPriceTokenForEntry =
    marketForEntry && lowPriceTokenIds.has(marketForEntry.id);
  const price = formatEntryPrice(trade.open_price, isLowPriceTokenForEntry);

  // Format leverage
  const leverage = formatLeverage(trade.leverage);

  // Format position size
  const sizeData = formatAmount(trade.collateral_amount, trade.leverage);

  // In your component, add this line after the other formatting
  const collateralData = formatCollateralAmount(trade.collateral_amount);

  // Type column values
  const type = getTradeSide(trade.long);
  const tradeType = getOrderType(trade.trade_type);

  // Track whether price has moved away from entry at least once
  const [hasPriceMoved, setHasPriceMoved] = useState(false);

  useEffect(() => {
      if (hasPriceMoved) return;

    const openPriceNum = parseFloat(trade.open_price);
    const currentPriceNum = marketPrice !== null && marketPrice !== undefined
      ? marketPrice
      : (currentPrice ? parseFloat(currentPrice) : null);

    if (openPriceNum && currentPriceNum && currentPriceNum !== openPriceNum) {
      setHasPriceMoved(true);
    }
  }, [hasPriceMoved, marketPrice, currentPrice, trade.open_price]);

  // Format PnL and percentage
  const pnlData = formatPnL(
    trade.collateral_amount,
    trade.is_open,
    trade.open_price,
    trade.long,
    trade.leverage,
    hasPriceMoved,
    trade.onchainPnl
  );

  // Specialized formatter for TP/SL that shows high precision for low-priced tokens.
  // For tiny prices on those tokens, use a CoinGecko-style "0.0₅7751" format where:
  // - subscript = number of extra zeros
  // - remaining digits are shown normally.
  const formatTpSlValue = (value, isLowPriceToken) => {
    if (!value && value !== 0) return null;
    
    const num = parseFloat(value);
    if (isNaN(num)) return null;
    
    const abs = Math.abs(num);

    // For low-priced tokens, use subscript-zero notation when there are many leading zeros
    // after the decimal, similar to CoinGecko.
    if (isLowPriceToken) {
      if (abs > 0 && abs < 1) {
        // Convert to a fixed decimal string with plenty of precision
        const raw = abs.toFixed(20); // "0.00000...digits"
        const decimalPart = raw.split(".")[1] || "";

        // Count leading zeros after "0."
        let zeroCount = 0;
        for (let i = 0; i < decimalPart.length; i++) {
          if (decimalPart[i] === "0") {
            zeroCount += 1;
          } else {
            break;
          }
        }

        // Remaining significant digits after the zeros (limit to 3 digits)
        const remaining = decimalPart.slice(zeroCount).slice(0, 3);

        // Fallback: if somehow we didn't get remaining digits, just show a small decimal
        if (!remaining) {
          return <>{num.toFixed(8)}</>;
        }

        return (
          <>
            0.0
            <sub>{zeroCount}</sub>
            {remaining}
          </>
        );
      }
      // For values >= 1 on low-priced tokens, show 6 decimals
      return <>{num.toFixed(6)}</>;
    }

    // For regular tokens, use standard formatting
    if (abs < 1) {
      const dec = num.toFixed(8).replace(/\.?0+$/, '');
      return <>{dec}</>;
    } else {
      return <>{num.toFixed(2)}</>;
    }
  };

  const formatTpSl = (tp, sl) => {
    // Detect if this is a low-price token for enhanced decimal precision
    const marketForTrade = getMarketByMarketIndex(trade.market_index);
    const isLowPriceToken = marketForTrade && lowPriceTokenIds.has(marketForTrade.id);

    // Prefer liquidation_price coming from the contract response when SL is missing.
    // First check the trade object, then any lazily-fetched fallback value.
    const rawLiquidationPrice =
      trade.trade_info?.liquidation_price ??
      trade.liquidation_price ??
      fallbackLiquidationPrice ??
      null;

    // -------- TP --------
    let formattedTp = null;
    if (tp !== null && tp !== undefined && tp !== "") {
      const tpNum = parseFloat(tp);
      // Contract uses 0 as "no TP" – don't show that as a real value
      if (!isNaN(tpNum) && tpNum !== 0) {
        formattedTp = formatTpSlValue(tp, isLowPriceToken);
      }
    }

    // -------- SL --------
    let formattedSl = null;
    let isLiquidation = false;

    const hasExplicitSl = sl !== null && sl !== undefined && sl !== "";
    const slNum = hasExplicitSl ? parseFloat(sl) : NaN;

    if (
      // No SL set or explicitly 0 → show liquidation price if we have it
      (!hasExplicitSl || (!isNaN(slNum) && slNum === 0)) &&
      rawLiquidationPrice
    ) {
      formattedSl = formatTpSlValue(rawLiquidationPrice, isLowPriceToken);
      isLiquidation = true;
    } else if (hasExplicitSl && !isNaN(slNum) && slNum !== 0) {
      // Normal SL set
      formattedSl = formatTpSlValue(sl, isLowPriceToken);
    }

    return {
      tp: formattedTp,
      sl: formattedSl,
      isLiquidation,
    };
  };
  //
  const tpSl = formatTpSl(trade.tp, trade.sl);

  const handleCloseTrade = async () => {
    try {
      if (!account) {
        toast.error("Please connect your wallet");
        return;
      }

      if (isEvm) {
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        
        console.log("🔍 Debug EVM close trade:", {
          originalIndex: trade.user_trade_index,
          parsedNumber: tradeIndexNumber,
          isNumber: typeof tradeIndexNumber === 'number',
          trade: trade
        });

        if (tradeIndexNumber === null || typeof tradeIndexNumber !== 'number') {
          toast.error("Invalid trade ID format");
          console.error("Invalid trade ID:", {
            original: trade.user_trade_index,
            parsed: tradeIndexNumber,
            type: typeof tradeIndexNumber
          });
          return;
        }
        
        const result = await closeTradeMarketEvm(tradeIndexNumber, true, dispatch);
        console.log("EVM close trade result:", result);
        
        if (!result.success) {
          throw new Error(result.error || "EVM close trade failed");
        }

        // SMART EVENT: Emit immediate user action
        window.dispatchEvent(new CustomEvent('userTradeAction', {
          detail: { 
            action: 'close_trade',
            tradeIndex: trade.user_trade_index,
            timestamp: Date.now()
          }
        }));
        
        // DELAYED CHECK: Verify state change after blockchain confirmation
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('blockchainStateCheck', {
            detail: { account, timestamp: Date.now() }
          }));
        }, 3000);
        
        return result;
      } else {
        // COSMOS WALLET
        const message = {
          close_trade: {
            trade_index: trade.user_trade_index,
          },
        };

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }

        // SMART EVENT: Emit immediate user action
        window.dispatchEvent(new CustomEvent('userTradeAction', {
          detail: { 
            action: 'close_trade',
            tradeIndex: trade.user_trade_index,
            timestamp: Date.now()
          }
        }));
        
        // DELAYED CHECK: Verify state change after blockchain confirmation
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('blockchainStateCheck', {
            detail: { account, timestamp: Date.now() }
          }));
        }, 3000);
        
        return res;
      }
    } catch (err) {
      console.error("Close trade error:", err);
      // toast.error(`Failed to close trade: ${err.message}`);
    }
  };

  // Get trade index number
  const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);

  const handleRowClick = (e) => {
    // Don't navigate if clicking on action buttons
    if (e.target.closest('button') || e.target.closest('.action-buttons')) {
      return;
    }
    // If parent provides onTradeClick (e.g. AllTradesTable), navigate to market page
    if (onTradeClick) {
      onTradeClick(trade);
      return;
    }
    // Otherwise open the detail modal (on market-specific TradingTable)
    setDetailModalOpen(true);
  };

  return (
    <>
      {/* ─── Mobile: one position per card ───
          Eleven columns of numbers cannot survive a phone viewport, and the
          900px floor turned this page into something you read by dragging it
          sideways. The card keeps the same handlers and the same figures, laid
          out as label-over-value pairs. */}
      <div
        onClick={handleRowClick}
        className="ce-tcard ce-tabular md:hidden"
        style={{ fontFamily: "General Sans" }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-bold truncate" style={{ color: "var(--ink)" }}>
                {market.split('/')[0]}
              </span>
              <span className="text-[11px]" style={{ color: "var(--ink-4)" }}>
                /{market.split('/')[1]}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span
                className="ce-chip"
                style={{
                  height: 20,
                  fontSize: 10,
                  background: trade.long ? "var(--tint-up)" : "var(--tint-down)",
                  color: trade.long ? "var(--on-tint-up)" : "var(--on-tint-down)",
                }}
              >
                {type}
              </span>
              <span className="text-[11px]" style={{ color: "var(--ink-3)" }}>
                {leverage} · {tradeType}
              </span>
              <span className="text-[11px]" style={{ color: "var(--ink-4)" }}>#{tradeIndexNumber}</span>
            </div>
          </div>

          <div className="text-right shrink-0">
            {pnlData.isReady ? (
              <>
                <div
                  className="ce-figure text-[16px] font-bold leading-tight"
                  style={{ color: pnlData.isProfit ? "var(--up)" : "var(--down)" }}
                >
                  ${pnlData.pnl}
                </div>
                <div
                  className="ce-figure text-[11.5px] font-medium"
                  style={{ color: pnlData.isProfit ? "var(--up)" : "var(--down)" }}
                >
                  ({pnlData.percentage}%)
                </div>
              </>
            ) : (
              <div className="animate-pulse h-5 w-16 rounded-full" style={{ background: "var(--surface-3)" }} />
            )}
          </div>
        </div>

        <div className="ce-tcard-grid">
          <div>
            <span className="ce-tcard-k">Entry</span>
            <span className="ce-tcard-v">{price}</span>
          </div>
          <div>
            <span className="ce-tcard-k">Margin</span>
            <span className="ce-tcard-v">{collateralData.amount}</span>
          </div>
          <div>
            <span className="ce-tcard-k">Size</span>
            <span className="ce-tcard-v">{sizeData.amount}</span>
          </div>
          <div>
            <span className="ce-tcard-k">Take profit</span>
            <span className="ce-tcard-v">
              {isLoadingTpSl ? "…" : tpSl.tp ? <>${tpSl.tp}</> : "—"}
            </span>
          </div>
          <div>
            <span className="ce-tcard-k">{tpSl.isLiquidation ? "Liquidation" : "Stop loss"}</span>
            <span className="ce-tcard-v">
              {isLoadingTpSl ? "…" : tpSl.sl ? <>${tpSl.sl}</> : "—"}
            </span>
          </div>
          <div>
            <span className="ce-tcard-k">Opened</span>
            <span className="ce-tcard-v" style={{ fontSize: 12 }}>{dateTimeFormatted.date}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <div
            className="action-buttons flex-1"
            onClick={(e) => {
              e.stopPropagation();
              setModalShow(true);
            }}
          >
            <WhiteButton text="Edit" className="w-full" />
          </div>
          {trade.is_open && (
            <div
              className="action-buttons flex-1"
              onClick={(e) => {
                e.stopPropagation();
                handleCloseTrade();
              }}
            >
              <RedButton text="Close" className="w-full" />
            </div>
          )}
        </div>
      </div>

      <div
        onClick={handleRowClick}
        /* Rows are separate raised cards, not divided lines. A hairline
           between rows says "these are cells of one grid"; a card says "this
           is one position, and you can act on it" — which is what these are.
           Hover lifts a pixel instead of tinting. */
        className={`ce-row-card ce-tabular hidden md:grid cursor-pointer text-sm whitespace-nowrap py-2 ${TRADES_ROW_PADDING_X}`}
        style={{
          fontFamily: "General Sans",
          gridTemplateColumns: TRADES_GRID_COLUMNS,
          minWidth: TRADES_MIN_WIDTH,
          /* Height follows the tallest cell — the actions column stacks Edit
             over Close, which needs more than one line. A fixed 46px pushed
             both buttons out through the bottom of the card. */
          minHeight: 46,
          marginBottom: 6,
        }}
      >
        {/* Trade Index */}
        <div className="flex items-center justify-center">
          <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{tradeIndexNumber}</span>
        </div>
        
        {/* Date & Time */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="text-xs" style={{ color: "var(--ink-3)" }}>{dateTimeFormatted.time}</div>
            <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{dateTimeFormatted.date}</div>
          </div>
        </div>
        
        {/* Pair */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{market.split('/')[0]}</div>
            <div className="text-xs" style={{ color: "var(--ink-3)" }}>{market.split('/')[1]}</div>
          </div>
        </div>
        
        {/* Type */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <span
              className="text-sm font-semibold"
              style={{ color: trade.long ? 'var(--up)' : 'var(--down)' }}
            >
              {type}
            </span>
            <span className="text-xs" style={{ color: "var(--ink-3)" }}>{tradeType}</span>
          </div>
        </div>
        
        {/* Entry Price */}
        <div className="flex items-center justify-center">
          <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{price}</span>
        </div>
        
        {/* Leverage */}
        <div className="flex items-center justify-center">
          <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{leverage}</span>
        </div>
        
        {/* Collateral Amount */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{collateralData.amount}</div>
            <div className="text-xs" style={{ color: "var(--ink-3)" }}>{collateralData.currency}</div>
          </div>
        </div>
        
        {/* Size */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{sizeData.amount}</div>
            <div className="text-xs" style={{ color: "var(--ink-3)" }}>{sizeData.currency}</div>
          </div>
        </div>
        
        {/* TP/SL */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center space-y-1 w-full">
            <div className="w-full text-center text-sm min-h-[20px] flex items-center justify-center" style={{ color: "var(--ink-3)" }}>
              {isLoadingTpSl ? (
                <div className="animate-pulse h-4 w-16 rounded-full" style={{ background: "var(--surface-3)" }} />
              ) : tpSl.tp ? (
                <span>${tpSl.tp}</span>
              ) : (
                "-"
              )}
            </div>
            <div className="w-full text-center text-sm min-h-[20px] flex items-center justify-center" style={{ color: "var(--ink-3)" }}>
              {isLoadingTpSl ? (
                <div className="animate-pulse h-4 w-16 rounded-full" style={{ background: "var(--surface-3)" }} />
              ) : tpSl.sl ? (
                <span>${tpSl.sl}</span>
              ) : (
                "-"
              )}
            </div>
          </div>
        </div>
        
        {/* PnL */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center space-y-0.5">
            {pnlData.isReady ? (
              <>
                <div className="flex items-center gap-1">
                  <div
                    /* No glow: weight and hue already carry profit vs loss. */
                    className="ce-figure text-[14px] font-bold"
                    style={{ color: pnlData.isProfit ? 'var(--up)' : 'var(--down)' }}
                  >
                    ${pnlData.pnl}
                  </div>
                  <span className="relative group/pnltip cursor-help">
                    <svg className="w-3 h-3 transition-colors flex-shrink-0" style={{ color: "var(--ink-4)" }} viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2.5a1 1 0 110 2 1 1 0 010-2zM6.75 7h1.5v4.5h-1.5V7z"/>
                    </svg>
                    <span
                      className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover/pnltip:opacity-100 transition-opacity z-50"
                      style={{ width: "200px" }}
                    >
                      <span
                        className="block rounded-lg text-[11px] leading-[1.4] px-2.5 py-2 text-center"
                        style={{
                          background: "var(--surface)",
                          color: "var(--ink-2)",
                          fontFamily: "General Sans",
                          fontWeight: 500,
                          boxShadow: "var(--float)",
                          whiteSpace: "normal",
                        }}
                      >
                        Price P&L only, fees not included. Click the trade for a full breakdown.
                      </span>
                      <span
                        className="block mx-auto"
                        style={{
                          width: 0, height: 0,
                          borderLeft: "6px solid transparent",
                          borderRight: "6px solid transparent",
                          borderTop: "6px solid var(--surface)",
                        }}
                      />
                    </span>
                  </span>
                </div>
                <div
                  className="ce-figure text-[12px] font-medium"
                  style={{ color: pnlData.isProfit ? 'var(--up)' : 'var(--down)' }}
                >
                  ({pnlData.percentage}%)
                </div>
              </>
            ) : (
              <>
                <div className="animate-pulse h-4 w-16 rounded-full mb-1" style={{ background: "var(--surface-3)" }} />
                <div className="animate-pulse h-3 w-12 rounded-full" style={{ background: "var(--surface-3)" }} />
              </>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div
              className="action-buttons w-[74px]"
            onClick={(e) => {
              e.stopPropagation();
              setModalShow(true);
            }}
          >
              {/* Editing a position is a secondary action. It was green,
                  which put it in the same visual class as "confirm" — a
                  neutral raised pill is what it actually is. */}
              <WhiteButton text="Edit" className="w-full" />
            </div>

            {trade.is_open && (
              <div
                className="action-buttons w-[74px]"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseTrade();
                }}
              >
                <RedButton text="Close" className="w-full" />
              </div>
            )}
          </div>
        </div>
      </div>
      <UpdateActionsModal
        isOpen={modalShow}
        setIsOpen={setModalShow}
        trade={trade}
      />
      <TradeDetailModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        trade={trade}
      />
    </>
  );
};

export default TradesRow;